import moment from 'moment';
import classNames from 'classnames';
import interpolate from 'pinterpolate';
import { FiExternalLink } from 'react-icons/fi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import useTitle from 'hooks/useTitle';
import useCurrentFiscalYear from 'hooks/useCurrentFiscalYear';
import { useDeleteNoticeMutation, useUserPermissions } from 'hooks/query/useUserInfo';
import { useFetchNoticesQuery } from 'hooks/query/useNoticeQueries';

import Table from 'components/common/table';
import PillTab from 'components/common/pillTab';
import PopOver from 'components/common/popover';
import Pagination from 'components/common/paginator';
import EditDelete from 'components/common/editDelete';
import TableHeader from 'components/common/table/TableHeader';

import * as routes from 'constants/routes';
import * as permissions from 'constants/permissions';
import { DISPLAY_DATE_FORMAT } from 'constants/date';
import { MINIMUM_PAGE_COUNT, MINIMUM_PAGE_ITEM } from 'constants/page';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  EVENTS_REFERENCE_PERIOD,
  EVENTS_REFERENCE_PERIOD_PILL_TABS,
} from 'constants/appConstants';
import { NOTICE_DESCRIPTION_LENGTH, TOOLTIP_LIMIT_LENGTH, NOTICE_TITLE_LENGTH } from 'constants/string';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { getIndexOfSN } from 'utils/table';
import { isObjectEmpty } from 'utils/object';
import { parse, stringify } from 'utils/queryParam';
import { getFormattedDate, isDateBetweenInclusive } from 'utils/date';
import { pluralize, withoutProtocolAndSubDomainURL } from 'utils/string';

import getCurrentReferencePeriod from 'utils/getCurrentReferencePeriod';

type NoticeQueryState = {
  referencePeriod: string;
  page?: number;
  size?: number;
};

type NoticeRecord = {
  id: number;
  title: string;
  description: string;
  redirectUrl: string;
  startDate: string;
  endDate: string;
};

type NoticeRow = {
  original: NoticeRecord;
  index: number;
  pageSize?: number;
};

type PopperData = {
  styles: Record<string, string | number>;
};

type QueryValue = string | string[] | Record<string, unknown> | Array<string | Record<string, unknown>> | undefined;

const getNumberQueryValue = (value: QueryValue, fallback: number): number => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const NoticeList: React.FC<RouteComponentProps> = (props) => {
  useTitle('Notices');

  const deleteNoticeMutation = useDeleteNoticeMutation();
  const userPermissions = useUserPermissions();

  const currentReferencePeriod = getCurrentReferencePeriod(props);
  const initialReferencePeriod =
    typeof currentReferencePeriod === 'string'
      ? currentReferencePeriod
      : EVENTS_REFERENCE_PERIOD.UPCOMING.toLowerCase();
  const [queries, setQueries] = useState<NoticeQueryState>({
    referencePeriod: initialReferencePeriod,
  });
  const [columnToSortBy, setColumnToSortBy] = useState('startDate');
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(false);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

  const currentFiscalYear = useCurrentFiscalYear() as Record<string, string>;

  const queryParams = parse(props.location.search, {
    decoder: (str) => decodeURIComponent(str),
  });

  const normalizedPage = getNumberQueryValue(queryParams.page, DEFAULT_PAGE_NUMBER);
  const normalizedSize = getNumberQueryValue(queryParams.size, DEFAULT_PAGE_SIZE);

  const { page, size, referencePeriod } = queries;

  const filters = useMemo(
    () => ({ ...queries, size: normalizedSize, page: normalizedPage }),
    [normalizedPage, normalizedSize, queries]
  );

  const { data, isLoading: isDataLoading } = useFetchNoticesQuery(filters, !isObjectEmpty(currentFiscalYear));

  const pageData = { page: data?.meta?.page ?? DEFAULT_PAGE_NUMBER, total: data?.meta?.total ?? 0 };
  const pageSize = normalizedSize;
  const pageCount = Math.ceil(pageData.total / normalizedSize);

  const noticeList = useMemo(() => {
    if (!data?.data) {
      return [];
    }

    return data.data.map((dataItem) => {
      const description = dataItem.description || '-';
      const redirectUrl = dataItem.redirectUrl || '-';
      const startDate = getFormattedDate(dataItem.startDate);
      const endDate = getFormattedDate(dataItem.endDate);

      return {
        ...dataItem,
        description,
        redirectUrl,
        startDate,
        endDate,
      };
    });
  }, [data]);
  const handleEdit = (row: NoticeRow) => {
    history.push(interpolate(routes.UPDATE_NOTICE, { id: row.original.id }));
  };

  const createPopperOptions = (customStyles: Record<string, string | number>) => {
    return {
      distance: 5,
      popperOptions: {
        modifiers: {
          flip: {
            enabled: false,
          },
          addZIndex: {
            enabled: true,
            order: 812,
            fn: (data: PopperData) => {
              return {
                ...data,
                styles: {
                  ...data.styles,
                  ...customStyles,
                },
              };
            },
          },
        },
      },
    };
  };

  const handleDelete = (row: NoticeRow) => {
    deleteNoticeMutation.mutate(row.original.id, {
      onSuccess: () => {
        toast.success({
          title: 'Success',
          message: 'Notice deleted successfully.',
        });

        if (noticeList.length === MINIMUM_PAGE_ITEM) {
          updateUrl({
            ...queries,
            page: DEFAULT_PAGE_NUMBER,
          });
        }
      },
    });
  };

  const getRowProps = (_state: object, rowInfo: NoticeRow) => {
    return {
      className: 'align-items-center d-flex',
      onClick: (e: React.MouseEvent) => {
        if (!userPermissions[permissions.NOTICES.UPDATE]) {
          return;
        }

        const url = interpolate(routes.UPDATE_NOTICE, { id: rowInfo.original.id });

        if (e.ctrlKey || e.metaKey) {
          window.open(url);
        } else {
          history.push(url);
        }
      },
    };
  };

  const columns = useMemo(
    () => [
      {
        className: 'notice-active',
        maxWidth: 6,
        Cell: (cell: NoticeRow) => {
          return (
            <PopOver
              interactive
              html={
                <div className="tooltip-info">
                  <p className="tooltip-info__title">Active</p>
                </div>
              }
            >
              <div
                className={classNames({
                  'notice-active notice-active__content': isDateBetweenInclusive(
                    getFormattedDate(moment()),
                    cell.original.startDate,
                    cell.original.endDate
                  ),
                })}
              />
            </PopOver>
          );
        },
      },
      {
        Header: <TableHeader label="SN." identifier="sn" />,
        accessor: 'sn',
        maxWidth: 56,
        sortable: false,
        filterable: false,
        Cell: (cell: NoticeRow) => {
          const index = getIndexOfSN(cell, pageSize, pageData);
          return <span className="capital-text employees__table-text">{index < 10 ? `0${index}` : index}</span>;
        },
      },
      {
        Header: (
          <TableHeader
            label="Title"
            identifier="title"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'title') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('title');
            }}
          />
        ),
        accessor: 'title',
        maxWidth: 300,
        Cell: (cell: NoticeRow) => {
          const isNotFirstRow = cell.index !== 0;
          const hasOneRowTop = cell.pageSize === 1 ? -20 : -40;
          const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top: hasOneRowTop });
          return cell.original.title.length > NOTICE_TITLE_LENGTH ? (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.title}</p>
                </div>
              }
              position="top-start"
              {...moreProps}
            >
              <div className="ellipsis">{cell.original.title}</div>
            </PopOver>
          ) : (
            <div>{cell.original.title}</div>
          );
        },
      },
      {
        Header: <TableHeader label="Description" identifier="description" />,
        accessor: 'description',
        sortable: false,
        filterable: false,
        maxWidth: 320,
        Cell: (cell: NoticeRow) => {
          const isNotFirstRow = cell.index !== 0;
          const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top: -20 });
          return cell.original.description.length > NOTICE_DESCRIPTION_LENGTH ? (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.description}</p>
                </div>
              }
              position="top-start"
              {...moreProps}
            >
              <div className="ellipsis">{cell.original.description}</div>
            </PopOver>
          ) : (
            <div>{cell.original.description}</div>
          );
        },
      },
      {
        Header: (
          <TableHeader
            label="Start Date"
            identifier="startDate"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'startDate') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('startDate');
            }}
          />
        ),
        Cell: (cell: NoticeRow) => {
          return <div>{getFormattedDate(cell.original.startDate, DISPLAY_DATE_FORMAT)}</div>;
        },
        accessor: 'startDate',
        maxWidth: 140,
      },
      {
        Header: (
          <TableHeader
            label="End Date"
            identifier="endDate"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'endDate') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('endDate');
            }}
          />
        ),
        Cell: (cell: NoticeRow) => {
          return <div>{getFormattedDate(cell.original.endDate, DISPLAY_DATE_FORMAT)}</div>;
        },
        accessor: 'endDate',
        maxWidth: 140,
      },
    ],
    [columnToSortBy, isAscendingOrdered, pageData, pageSize]
  );

  const getLinkCell = () => {
    return {
      Header: <TableHeader label="Link" identifier="redirectUrl" />,
      accessor: 'redirectUrl',
      sortable: false,
      filterable: false,
      maxWidth: 200,
      Cell: (cell: NoticeRow) => {
        const isNotFirstRow = cell.index !== 0;
        const linkExceedsLimit = cell.original.redirectUrl.length > TOOLTIP_LIMIT_LENGTH;
        const top = linkExceedsLimit ? -40 : -20;
        const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top });
        return cell.original.redirectUrl !== '-' ? (
          <PopOver
            interactive
            html={
              <div className="tooltip-info word-break">
                <p className="tooltip-info__title">{cell.original.redirectUrl}</p>
              </div>
            }
            position="top-start"
            visible={Boolean(withoutProtocolAndSubDomainURL(cell.original.redirectUrl).length)}
            {...moreProps}
          >
            <a
              href={cell.original.redirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center table-link"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="table-link__text ellipsis">
                {withoutProtocolAndSubDomainURL(cell.original.redirectUrl)}
              </div>
              <div className="center">
                <FiExternalLink className="table-link__icon" size={12} />
              </div>
            </a>
          </PopOver>
        ) : (
          <>{cell.original.redirectUrl}</>
        );
      },
    };
  };

  const getDeleteCell = () => {
    const canEdit = Boolean(userPermissions[permissions.NOTICES.UPDATE]);
    const canDelete = Boolean(userPermissions[permissions.NOTICES.DELETE]);
    const canEditOrDelete = canEdit || canDelete;

    return {
      Header: '',
      width: 6,
      className: 'margin-left-auto',
      Cell: (row: NoticeRow) =>
        canEditOrDelete ? (
          <span
            className="verticle-ellipse cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <PopOver
              interactive
              open={openPopoverId === row.original.id}
              onRequestClose={() => setOpenPopoverId(null)}
              theme="light"
              size="regular"
              customClass="overflow-menu__regular"
              position="left-start"
              disableOnScroll={true}
              popperOptions={{
                modifiers: {
                  addZIndex: {
                    enabled: true,
                    order: 810,
                    fn: (data: PopperData) => ({
                      ...data,
                      styles: {
                        ...data.styles,
                        zIndex: 10,
                      },
                    }),
                  },
                },
              }}
              html={
                <EditDelete
                  onClose={() => setOpenPopoverId(null)}
                  onDelete={() => handleDelete(row)}
                  onEdit={() => handleEdit(row)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  type="notice"
                  title={row.original.title}
                />
              }
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopoverId(openPopoverId === row.original.id ? null : row.original.id);
                }}
              >
                <BsThreeDotsVertical size={20} />
              </span>
            </PopOver>
          </span>
        ) : null,
    };
  };

  const updateUrl = useCallback(
    (value: NoticeQueryState) => {
      const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });

      history.push({
        pathname: props.location.pathname,
        search: queryParam,
      });
    },
    [props.location.pathname]
  );

  useEffect(() => {
    updateUrl({
      ...queries,
    });
  }, [referencePeriod, page, size, props.location.search, queries, updateUrl]);

  const onPageChange = (selectedPage: number) => {
    setQueries((value) => ({ ...value, page: selectedPage }));
  };

  const handleApplyFilter = (filters: Pick<NoticeQueryState, 'referencePeriod'>) => {
    setQueries({ ...filters, page: DEFAULT_PAGE_NUMBER, size: DEFAULT_PAGE_SIZE });
  };

  const onPageSizeChange = (selectedSize: number) => {
    setQueries((value) => ({ ...value, page: DEFAULT_PAGE_NUMBER, size: selectedSize }));
  };

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Notices</div>
          <div className="table-subtitle">
            Showing {noticeList.length} {pluralize('Result', noticeList.length)}
          </div>
        </div>
        <div className="table-header__right">
          <PillTab
            tabs={EVENTS_REFERENCE_PERIOD_PILL_TABS}
            onTabClick={(value) => handleApplyFilter({ referencePeriod: String(value) })}
            isActive={(value) => String(value) === queries.referencePeriod.toLowerCase()}
          />
        </div>
      </div>
      <div className={classNames('full-scope-card__content')}>
        <>
          <Table
            loading={isDataLoading}
            data={noticeList}
            columns={[...columns, getLinkCell(), getDeleteCell()]}
            showPagination={false}
            noDataText="No notices to show."
            getTrProps={getRowProps}
            customClassName="notice-table"
          />

          <Pagination
            pageData={pageData}
            pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      </div>
    </div>
  );
};

export default NoticeList;
